<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
// PHASE1: Filter Payments by deposit bank IDs from GET /api/payments/banks.
// PHASE2: Destination Bank Account filtering remains deferred.
import {
    computed, onMounted, ref, watch,
} from 'vue';
import Checkbox from 'primevue/checkbox';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import Popover from 'primevue/popover';
import {
    savePaymentBankIds,
    type PaymentBankSelection,
    usePaymentBankOptions,
} from '@/composables/usePaymentBankOptions';

const props = defineProps<{
    modelValue: PaymentBankSelection;
}>();

const emit = defineEmits<{
    'update:modelValue': [value: PaymentBankSelection];
}>();

const popoverRef = ref();
const search = ref('');
const { options, loadOptions } = usePaymentBankOptions();

const normalizedSelected = computed(() => {
    if (props.modelValue === null) return [];

    return props.modelValue.filter(
        (id) => options.value.some((option) => option.id === id),
    );
});

// Selections are staged in a draft so the panel filters and closes only on Apply.
const draft = ref<PaymentBankSelection>(null);

const draftSelected = computed(() => {
    if (draft.value === null) return [];

    return draft.value.filter(
        (id) => options.value.some((option) => option.id === id),
    );
});

const allSelected = computed(() => (
    options.value.length > 0
    && draft.value === null
));

const partiallySelected = computed(() => (
    draft.value !== null
    && draftSelected.value.length > 0
    && draftSelected.value.length < options.value.length
));

const visibleOptions = computed(() => {
    const term = search.value.trim().toLowerCase();
    if (!term) return options.value;
    return options.value.filter((option) => option.label.toLowerCase().includes(term));
});

const triggerValue = computed(() => {
    if (options.value.length === 0 || props.modelValue === null) return 'All';
    return `${normalizedSelected.value.length} of ${options.value.length}`;
});

function isSelected(id: number): boolean {
    return draft.value === null || draftSelected.value.includes(id);
}

function updateSelection(selection: PaymentBankSelection) {
    savePaymentBankIds(selection);
    emit('update:modelValue', selection);
}

function syncDraft() {
    draft.value = props.modelValue === null ? null : [...normalizedSelected.value];
}

function updateOptionSelection(id: number, checked: boolean) {
    const selected = draft.value === null
        ? options.value.map((option) => option.id)
        : [...draftSelected.value];
    // PHASE1
    const next = checked
        ? (selected.includes(id) ? selected : [...selected, id])
        : selected.filter((selectedId) => selectedId !== id);

    draft.value = next.length === options.value.length ? null : next;
}

function selectAll(checked: boolean) {
    // PHASE1
    draft.value = checked ? null : [];
}

function applySelection() {
    updateSelection(draft.value);
    popoverRef.value?.hide();
}

function cancelSelection() {
    syncDraft();
    popoverRef.value?.hide();
}

// Refreshed before the panel opens rather than on the popover's show event, so the first frame
// already holds the applied selection.
function togglePanel(event: MouseEvent) {
    if (!popoverRef.value?.visible) {
        search.value = '';
        syncDraft();
    }

    popoverRef.value?.toggle(event);
}

watch(options, (nextOptions) => {
    if (nextOptions.length === 0 || props.modelValue === null) return;

    const validIds = new Set(nextOptions.map((option) => option.id));
    const nextSelected = [...new Set(props.modelValue.filter((id) => validIds.has(id)))];
    const nextSelection = nextSelected.length === nextOptions.length ? null : nextSelected;

    if (
        nextSelection === null
        || nextSelection.length !== props.modelValue.length
        || nextSelection.some((id, index) => id !== props.modelValue[index])
    ) {
        updateSelection(nextSelection);
    }
}, { immediate: true });

onMounted(() => {
    void loadOptions();
});
</script>

<template>
    <div v-if="options.length > 1" class="bank-account-filter">
        <button
            class="bank-account-filter__trigger"
            type="button"
            aria-haspopup="dialog"
            @click="togglePanel"
        >
            <span class="bank-account-filter__trigger-label">Filter by Bank:</span>
            <span class="bank-account-filter__trigger-value">{{ triggerValue }}</span>
            <svg class="bank-account-filter__trigger-chevron" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true">
                <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>

        <Popover
            ref="popoverRef"
            :pt="{ root: { class: 'bank-account-filter-popover popover-align-end' } }"
        >
            <div
                class="bank-account-filter__panel"
                role="dialog"
                aria-label="Bank Accounts"
            >
                <div class="bank-account-filter__title">
                    Bank Account
                </div>

                <div
                    class="bank-account-filter__divider
                                bank-account-filter__divider--header"
                />

                <IconField class="bank-account-filter__search">
                    <InputText
                        v-model="search"
                        type="text"
                        name="payment-bank-search"
                        autocomplete="off"
                        placeholder="Search for a Bank Account..."
                    />
                    <InputIcon
                        v-if="search"
                        class="pi pi-times"
                        @click="search = ''"
                    />
                </IconField>

                <div
                    class="bank-account-filter__divider
                                bank-account-filter__divider--options"
                />

                <div
                    v-if="!search"
                    class="bank-account-filter__selectall"
                >
                    <Checkbox
                        inputId="payment-bank-select-all"
                        binary
                        :model-value="allSelected"
                        :indeterminate="partiallySelected"
                        @update:model-value="selectAll($event)"
                    />
                    <label
                        class="bank-account-filter__selectall-label"
                        for="payment-bank-select-all"
                    >
                        Select All
                    </label>
                </div>

                <div class="bank-account-filter__fields">
                    <ul class="bank-account-filter__list">
                        <li
                            v-for="option in visibleOptions"
                            :key="option.id"
                            class="bank-account-filter__item"
                        >
                            <Checkbox
                                :inputId="`payment-bank-${option.id}`"
                                binary
                                :model-value="isSelected(option.id)"
                                @update:model-value="updateOptionSelection(option.id, $event)"
                            />
                            <label
                                class="bank-account-filter__item-label"
                                :for="`payment-bank-${option.id}`"
                            >
                                {{ option.label }}
                            </label>
                        </li>
                    </ul>
                </div>

                <div class="bank-account-filter__footer">
                    <button
                        type="button"
                        class="bank-account-filter__footer-action
                            bank-account-filter__footer-action--secondary"
                        @click="cancelSelection"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="bank-account-filter__footer-action
                            bank-account-filter__footer-action--primary"
                        @click="applySelection"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </Popover>
    </div>
</template>

<style lang="scss" scoped>
.bank-account-filter {
    position: static;
    flex: 0 0 auto;

    &__trigger {
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        gap: 6px;
        height: 46px;
        padding: 0 20px;
        border: 1px solid $border-gray-color;
        border-radius: 10px;
        background: $white-color;
        font-family: $font-family-archivo;
        font-size: 16px;
        font-weight: 400;
        line-height: 17px;
        color: $black-color;
        text-align: left;
        white-space: nowrap;
        cursor: pointer;
    }

    &__trigger-label {
        color: rgba($black-color, 0.7);
    }

    &__trigger-value {
        color: $black-color;
        font-weight: 500;
    }

    &__trigger-chevron {
        color: rgba(0, 0, 0, .6) !important;
        flex-shrink: 0;
        margin-left: 2px;
    }

    &__panel {
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        width: 250px;
        height: max-content;
        padding: 20px;
        overflow: hidden;
        border: 1px solid $border-gray-color;
        border-radius: 10px;
        background: $white-color;
        box-shadow: 8px 10px 50px rgba(0, 0, 0, 0.05);
    }

    &__title {
        font-family: $font-family-archivo;
        font-size: 16px;
        font-weight: 400;
        line-height: 18px;
        color: $black-color;
    }

    &__divider {
        flex-shrink: 0;
        height: 2px;
        background: $border-gray-color;

        &--header {
            margin-top: 10px;
        }

        &--options {
            margin-top: 15px;
        }
    }

    &__search {
        width: 100%;
        margin-top: 15px;

        :deep(.p-icon-field),
        :deep(.p-iconfield) {
            width: 100%;
        }

        :deep(.p-inputtext) {
            width: 100%;
            height: 40px;
            padding: 11px 20px !important;
            border: 1px solid $border-gray-color !important;
            border-radius: 20px;
            background: $white-color !important;
            box-shadow: none !important;
            font-family: $font-family-archivo;
            font-size: 12px;
            font-weight: 400;
            color: rgba($black-color, 0.7);
            caret-color: $black-color;

            &::placeholder {
                color: rgba($black-color, 0.7);
            }

            &:focus {
                border-color: $main-orange !important;
                box-shadow: none !important;
            }
        }

        :deep(.p-inputicon.pi-times) {
            cursor: pointer;
        }
    }

    &__selectall {
        display: flex;
        align-items: center;
        gap: 0;
        height: 16px;
        margin-top: 16px;
        cursor: pointer;

        :deep(.p-checkbox) {
            margin-right: 12px;
        }
    }

    &__selectall-label {
        font-family: $font-family-archivo;
        font-size: 15px;
        font-weight: 400;
        line-height: 16px;
        color: $black-color;
        cursor: pointer;
    }

    &__fields {
        flex: 1 1 auto;
        width: 100%;
        min-height: 0;
        margin-top: 11px;
        overflow-y: auto;
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__item {
        display: flex;
        align-items: center;
        gap: 0;
        width: 100%;
        min-height: 17px;

        :deep(.p-checkbox) {
            margin-right: 12px;
        }
    }

    &__item-label {
        font-family: $font-family-archivo;
        font-size: 15px;
        font-weight: 400;
        line-height: 16px;
        color: $black-color;
        cursor: pointer;
        user-select: none;
    }

    &__footer {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 15px;
        padding-top: 15px;
        border-top: 2px solid $border-gray-color;
    }

    &__footer-action {
        min-width: 76px;
        height: 34px;
        padding: 0 14px;
        border-radius: 999px;
        font-family: $font-family-archivo;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;

        &--secondary {
            border: 1px solid $border-gray-color;
            background: $white-color;
            color: rgba($black-color, 0.72);
        }

        &--primary {
            border: 1px solid transparent;
            background: $main-orange;
            color: $white-color;
        }
    }

}

:global(.p-popover.bank-account-filter-popover) {
    --p-popover-gutter: 14px;
    --p-popover-content-padding: 0;
    background: transparent;
    border: 0 none;
    box-shadow: none;
}

:global(.p-popover.bank-account-filter-popover::before),
:global(.p-popover.bank-account-filter-popover::after) {
    display: none !important;
}

/* Checkbox styling */
:deep(.p-checkbox),
:deep(.p-checkbox-box) {
    width: 16px !important;
    height: 16px !important;
}

:deep(.p-checkbox-box) {
    border-radius: 3px !important;
    background: $white-color !important;
    border: 1px solid rgba($black-color, 0.3) !important;
    box-shadow: none !important;
}

:deep(.p-checkbox-icon) {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
}

:deep(.p-checkbox-checked .p-checkbox-box),
:deep(.p-checkbox-checked.p-variant-filled .p-checkbox-box) {
    background: $main-orange !important;
    border-color: $main-orange !important;
}

:deep(.p-checkbox:not(.p-disabled):hover .p-checkbox-box) {
    border-color: $main-orange !important;
}

:deep(.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box) {
    background: $main-orange-hover !important;
    border-color: $main-orange-hover !important;
}

:deep(.p-checkbox-checked .p-checkbox-icon) {
    color: $white-color !important;
}

:deep(.p-checkbox[data-p-indeterminate='true'] .p-checkbox-box) {
    background: rgba($black-color, 0.7) !important;
    border-color: rgba($black-color, 0.7) !important;
}

:deep(.p-checkbox[data-p-indeterminate='true'] .p-checkbox-icon) {
    color: $white-color !important;
    width: 12px !important;
    height: 12px !important;
}

:deep(.p-checkbox[data-p-indeterminate='true']:not(.p-disabled):hover .p-checkbox-box) {
    background: rgba($black-color, 0.8) !important;
    border-color: rgba($black-color, 0.8) !important;
}
</style>
